// 渠道推广追踪 - traceId 管理
// 单一 key 存储，entry 内记录 uid，存储时不依赖 uid，读取时校验归属

import { getParamsFromHash } from "./url";
import { resolveUid } from "./host-login-state";

const STORAGE_KEY = "charge_trace_id";
const TTL = 24 * 60 * 60 * 1000; // 24h

interface TraceIdEntry {
  traceId: string;
  storedAt: number;
  /** 存储时的 UID，未登录时为空字符串 */
  uid: string;
}

/**
 * 同步获取当前有效的 UID，无效时返回空字符串
 *
 * 走 resolveUid 而不是直读 sessionStorage：切换账号时 applyHostLoginState 会把残留的
 * sessionUid 清掉，此刻直读拿到空串，下面 getTraceId 的 UID 归属校验就会因为
 * `currentUid &&` 短路而被跳过，B 账号会读到 A 账号的 traceId。宿主在 URL 上声明的
 * uid 是此时唯一可用的身份来源。
 */
function getCurrentUidSync(): string {
  return resolveUid(() => sessionStorage.getItem("sessionUid") || "");
}

/**
 * 从 URL 中解析 traceId 参数
 * 复用 getParamsFromHash 以兼容 ?/& 混合的参数格式
 * 如：#configPage?pageCode=xxx&traceId=yyy?vin=zzz
 */
export function parseTraceIdFromUrl(): string | null {
  const value = getParamsFromHash("traceId");
  return value || null;
}

/**
 * 存储 traceId。
 * 不再依赖 UID 才能存储——未登录时也能存，登录后读取时校验归属。
 */
export function setTraceId(traceId: string): void {
  if (!traceId) return;
  try {
    const entry: TraceIdEntry = {
      traceId,
      storedAt: Date.now(),
      uid: getCurrentUidSync(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage 写入失败
  }
}

/**
 * 获取当前用户的 traceId。
 * - 过期（24h）→ 清除并返回 null
 * - 存储时有 UID 且与当前 UID 不一致 → 说明切换了账号，清除并返回 null
 * - 存储时无 UID（未登录时写入）→ 视为属于当前用户，正常返回
 */
export function getTraceId(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const entry: TraceIdEntry = JSON.parse(raw);

    if (Date.now() - entry.storedAt >= TTL) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // 校验 UID 归属
    const currentUid = getCurrentUidSync();
    if (entry.uid && currentUid && entry.uid !== currentUid) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return entry.traceId;
  } catch {
    // localStorage 读取失败
  }
  return null;
}

/** 清除 traceId */
export function clearTraceId(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
